import OrderOptimizerListingRouteQueryParams from "../../../common/routes/types/order-optimizer-listing-route-params";
import OrderOptimizerTaxiListingFilter, {
  OrderOptimizerTaxiListingIgnoreDriverRoutesFilter,
  OrderOptimizerTaxiListingMaximumDelayFilter,
  OrderOptimizerTaxiListingMaximumHaltingTimeFilter,
  OrderOptimizerTaxiListingOrderHumanIdFilter,
  OrderOptimizerTaxiListingStatusFilter,
} from "../common/types/order-optimizer-taxi-listing-filter";
import OrderOptimizerTaxiListingFilterType from "../common/types/order-optimizer-taxi-listing-filter-type";
import orderOptimizerTaxiListingFiltersHelper from "./order-optimizer-taxi-listing-filters.helper";

const getPage = (
  routeQueryFilterParams: OrderOptimizerListingRouteQueryParams,
): number | undefined => {
  return routeQueryFilterParams.page
    ? Number(routeQueryFilterParams.page)
    : undefined;
};

const getPageSize = (
  routeQueryFilterParams: OrderOptimizerListingRouteQueryParams,
): number | undefined => {
  return routeQueryFilterParams.pageSize
    ? Number(routeQueryFilterParams.pageSize)
    : undefined;
};

const getFilters = (
  routeQueryFilterParams: OrderOptimizerListingRouteQueryParams,
): OrderOptimizerTaxiListingFilter[] => {
  const filters: OrderOptimizerTaxiListingFilter[] = [];

  if (routeQueryFilterParams.orderHumanId) {
    const orderHumanIdFilter: OrderOptimizerTaxiListingOrderHumanIdFilter = {
      type: OrderOptimizerTaxiListingFilterType.ORDER_HUMAN_ID,
      value: Number(routeQueryFilterParams.orderHumanId),
    };

    filters.push(orderHumanIdFilter);
  }

  if (routeQueryFilterParams.maximumDelay) {
    const maximumDelayFilter: OrderOptimizerTaxiListingMaximumDelayFilter = {
      type: OrderOptimizerTaxiListingFilterType.MAXIMUM_DELAY,
      value: Number(routeQueryFilterParams.maximumDelay),
    };

    filters.push(maximumDelayFilter);
  }

  if (routeQueryFilterParams.maximumHaltingTime) {
    const maximumHaltingTimeFilter: OrderOptimizerTaxiListingMaximumHaltingTimeFilter =
      {
        type: OrderOptimizerTaxiListingFilterType.MAXIMUM_HALTING_TIME,
        value: Number(routeQueryFilterParams.maximumHaltingTime),
      };

    filters.push(maximumHaltingTimeFilter);
  }

  if (routeQueryFilterParams.ignoreDriverRoutes) {
    const ignoreDriverRoutesFilter: OrderOptimizerTaxiListingIgnoreDriverRoutesFilter =
      {
        type: OrderOptimizerTaxiListingFilterType.IGNORE_DRIVER_ROUTES,
        value: routeQueryFilterParams.ignoreDriverRoutes,
      };

    filters.push(ignoreDriverRoutesFilter);
  }

  if (routeQueryFilterParams.status) {
    const value =
      orderOptimizerTaxiListingFiltersHelper().getStatusValueFromRouteQueryParam(
        routeQueryFilterParams.status,
      );

    if (value) {
      const statusFilter: OrderOptimizerTaxiListingStatusFilter = {
        type: OrderOptimizerTaxiListingFilterType.STATUS,
        value,
      };

      filters.push(statusFilter);
    }
  }

  return filters;
};

const createRouteQueryParams = (
  filters: OrderOptimizerTaxiListingFilter[],
  page: number,
  pageSize: number,
): OrderOptimizerListingRouteQueryParams => {
  const orderHumanId = filters.find(
    (filter) =>
      filter.type === OrderOptimizerTaxiListingFilterType.ORDER_HUMAN_ID,
  )?.value as OrderOptimizerTaxiListingOrderHumanIdFilter["value"] | undefined;

  const maximumDelay = filters.find(
    (filter) =>
      filter.type === OrderOptimizerTaxiListingFilterType.MAXIMUM_DELAY,
  )?.value as OrderOptimizerTaxiListingMaximumDelayFilter["value"] | undefined;

  const maximumHaltingTime = filters.find(
    (filter) =>
      filter.type === OrderOptimizerTaxiListingFilterType.MAXIMUM_HALTING_TIME,
  )?.value as
    | OrderOptimizerTaxiListingMaximumHaltingTimeFilter["value"]
    | undefined;

  const ignoreDriverRoutes = filters.find(
    (filter) =>
      filter.type === OrderOptimizerTaxiListingFilterType.IGNORE_DRIVER_ROUTES,
  )?.value as
    | OrderOptimizerTaxiListingIgnoreDriverRoutesFilter["value"]
    | undefined;

  const status = filters.find(
    (filter) => filter.type === OrderOptimizerTaxiListingFilterType.STATUS,
  )?.value as OrderOptimizerTaxiListingStatusFilter["value"] | undefined;

  const routeQueryParams: OrderOptimizerListingRouteQueryParams = {
    page: page ? page.toString() : undefined,
    pageSize: pageSize ? pageSize.toString() : undefined,
    orderHumanId: orderHumanId ? orderHumanId.toString() : undefined,
    maximumDelay: maximumDelay ? maximumDelay.toString() : undefined,
    maximumHaltingTime: maximumHaltingTime
      ? maximumHaltingTime.toString()
      : undefined,
    ignoreDriverRoutes: ignoreDriverRoutes,
    status: status
      ? orderOptimizerTaxiListingFiltersHelper().getStatusRouteQueryParamFromValue(
          status,
        )
      : undefined,
  };

  return routeQueryParams;
};

const orderOptimizerTaxiListingFiltersService = {
  getPage,
  getPageSize,
  getFilters,
  createRouteQueryParams,
};

export default orderOptimizerTaxiListingFiltersService;
